-- | This module implements the "autofill select" widget - a select widget
-- | which, when an item is selected, triggers a request to the backend for
-- | additional autofill data to load into the form.
module Widgets.Form.AutoFillSelect where

import Common

import API.Autofill (AutofillArgs, AutofillType(..))
import API.Expectations (GroupCourse, courseListSelAff, expCourseToGroup)
import API.SelEmployer (PosConEmployer, EmployerID, contactSelAff, employerPosConSearchSelAff, getEmployer, positionSelAff)
import API.Types (ID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Data.Argonaut as J
import Data.Array (catMaybes, mapWithIndex, updateAt)
import Data.Array as Arr
import Data.Maybe (isNothing, maybe)
import Error (reportError, tagAction, tagWidget)
import Foreign.Object as O
import GraphQL.Join (runGraphQL)
import GraphQL.Shape (QueryField(..))
import GraphQL.Type (dummyId, innerId, maybeId)
import Internal.ConcurUtil (loopInfinitely)
import Internal.JsonUtil (assertObject, numberToString)
import Internal.Util (justIf)
import Unsafe.Coerce (unsafeCoerce)
import Widgets.BetterSelect (GroupFunc, SearchAff, Select, SelectOption, defSelectConfig, emptySearchAff, groupOptsByKey, initGroupedSelect', initSelect', mapSearchAff, rankByLabel, selectWidget, selectedItem, setSearchAff, setSelected)
import Widgets.EmployerModal (employerCreateModal)
import Widgets.Form.Class (class FormWidget, FormContext, FormWidgetAction(..), FormWidgetResult, fieldCanUpdate, fieldId, formWidgetAction, formWidgetState)

courseAutoFillSelect :: AutoFillSelectInit -> AutoFillSelect GroupCourse
courseAutoFillSelect init = initAutoFillSelect init
    { label: lookupTranslation "course-form-course"
    , types: [CoursesAF]
    , funcs:
        { apply: \fc (c :: GroupCourse) -> _ { courses = [
            { index: fc.curIndex, id: c.id }
        ] }
        , itemId: innerId <<< _.id
        , itemLabel: _.label
        , load: \fc id -> do
            doc <- fc.doc
            c <- Arr.find (\c -> innerId c.id == id) doc.courses
            pure $ expCourseToGroup c
        }
    , selAff: \fc -> courseListSelAff (maybe dummyId _.student.id fc.doc)
    , groupFunc: Just $ groupOptsByKey _.group_label _.group_label
    , subsels: []
    , buttons: \_ -> []
    }

employerAutoFillSelect :: AutoFillSelectInit -> AutoFillSelect PosConEmployer
employerAutoFillSelect init = initAutoFillSelect init
    { label: lookupTranslation "employer-form-employer"
    , types: [EmployerAF, ContactAF, PositionAF]
    , funcs:
        { apply: \_ e -> _ { employer_id = Just e.id }
        , itemId: innerId <<< _.id
        , itemLabel: _.company
        , load: \fc _ -> _.employer =<< fc.doc
        }
    , selAff: \fc -> employerPosConSearchSelAff
        (maybe dummyId _.id fc.doc) (maybe dummyId _.student.id fc.doc)
    , groupFunc: Nothing
    , subsels:
        [ initSubSelect
            { id: "position"
            , label: lookupTranslation "position"
            , funcs:
                { apply: \_ p -> _ { position_id = Just p.id }
                , itemId: innerId <<< _.id
                , itemLabel: _.label
                , load: \fc _ -> _.position =<< fc.doc
                }
            , selAff: \fc employer -> positionSelAff
                (maybeId $ _.id <$> fc.doc)
                (maybeId $ _.student.id <$> fc.doc)
                employer.id
            , autoSel: \_ e -> maybe ASDeselect AutoSelect (Arr.head =<< map _.positions e)
            }
        , initSubSelect
            { id: "contact"
            , label: lookupTranslation "employer-contact"
            , funcs:
                { apply: \_ c -> _ { contact_id = Just c.id }
                , itemId: innerId <<< _.id
                , itemLabel: _.name
                , load: \fc _ -> _.contact =<< fc.doc
                }
            , selAff: \fc employer -> contactSelAff
                (maybeId $ _.id <$> fc.doc)
                (maybeId $ _.student.id <$> fc.doc)
                employer.id
            , autoSel: \_ e -> maybe ASDeselect AutoSelect (Arr.head =<< map _.contacts e)
            }
        ]
    , buttons
    }
  where
    buttons :: Maybe PosConEmployer -> Array (AFSelectButton PosConEmployer)
    buttons Nothing = [addBtn]
    buttons (Just emp) = catMaybes
        [ justIf (isNothing emp.deleted_at) $ \fc -> editBtn fc emp
        , Just $ addBtn ]

    addBtn :: FormContext -> Widget HTML (Maybe PosConEmployer)
    addBtn fc = do
        let btn = D.button
                [ P.className "btn employer-add-btn", P.onClick ]
                [ D.text $ lookupTranslation $ "add-employer"
                ]
        _ <- btn
        eid <- employerCreateModal Nothing <|> loopInfinitely btn
        case eid of
            Just id -> loadEmployer fc id
            Nothing -> pure Nothing

    editBtn :: FormContext -> PosConEmployer -> Widget HTML (Maybe PosConEmployer)
    editBtn fc emp = do
        let btn = D.button
                [ P.className "btn employer-edit-btn", P.onClick ]
                [ D.text $ lookupTranslation "edit-employer" <> emp.company
                ]
        _ <- btn
        eid <- employerCreateModal (Just emp.id) <|> loopInfinitely btn
        case eid of
            Just id -> loadEmployer fc id
            Nothing -> pure Nothing

    loadEmployer :: FormContext -> EmployerID -> Widget HTML (Maybe PosConEmployer)
    loadEmployer fc id = do
        let tagctx = tagWidget "AutoFillSelect" <<< tagAction "while loading added/edited employer info"
        empM <- tagctx <$> affAction (runGraphQL $ getEmployer id
                (maybeId $ _.id <$> fc.doc)
                (maybeId $ _.student.id <$> fc.doc))
        case empM of
            Right emp -> pure $ Just emp
            Left err -> do
                _ <- liftEffect $ reportError err
                pure Nothing


initAutoFillSelect :: forall a. AutoFillSelectInit -> AutoFillSelectDefinition a -> AutoFillSelect a
initAutoFillSelect { id } def = AutoFillSelect
    { id
    , select: initGroupedSelect' (id <> "-af-select") emptySearchAff def.groupFunc $ defSelectConfig
        { placeholder = Just $ lookupTranslation "select-choose-search" }
    , subsels: def.subsels
    , def: def
    , fillAnyway: false
    }


-- | Config we get from the form layout. Combined with `AutoFillSelectFuncs` by
-- | `initAutoFillSelect` to initialize an autofill select.
type AutoFillSelectInit =
    { id :: String }

type AutoFillSelectDefinition a =
    { selAff :: FormContext -> SearchAff a
    , groupFunc :: Maybe (GroupFunc a)
    , funcs :: AutoFillSelectFuncs a
    , subsels :: Array (SubSelect a)
    , label :: String
    , buttons :: Maybe a -> Array (AFSelectButton a)
    , types :: Array AutofillType
    -- ^ A list of things that will be autofilled from this select.
    }
type AFSelectButton a = FormContext -> Widget HTML (Maybe a)

-- Functions needed by the frontend based on what type of select it is.
type AutoFillSelectFuncs a =
    { apply :: FormContext -> a -> AutofillArgs -> AutofillArgs
    -- ^ Function to apply the currently selected item to the given `AutofillArgs`
    , itemId :: a -> ID
    -- ^ Function to get an ID from the selected item.
    , itemLabel :: a -> String
    -- ^ Function to get a label from the selected item.
    , load :: FormContext -> ID -> Maybe a
    -- ^ Function to load selected item
    }


-- | Defines a sub-select's configuration.
-- |
-- | `a` here is the `a` type of the autofill select containing this (e.g.,
-- | `SelEmployer`). Before this is added to an autofill select, the `b`
-- | sub-select type must be erased by turning this into a `SubSelect` with
-- | `initSubSelect`.
type SubSelectDefiniton a b =
    { id :: String
    , label :: String
    , funcs :: AutoFillSelectFuncs b
    , selAff :: FormContext -> a -> SearchAff b
    -- ^ Get a `SearchAff` for this sub-select given a selection in the main
    -- select.
    , autoSel :: FormContext -> Maybe a -> AutoSelAction b
    -- ^ Allows a sub-select to automatically select something based on a
    -- change to the main selection.
    }

data AutoSelAction a = AutoSelect a | ASNoChange | ASDeselect
instance asaFunctor :: Functor AutoSelAction where
    map f (AutoSelect a) = AutoSelect $ f a
    map _ ASDeselect = ASDeselect
    map _ ASNoChange = ASNoChange

-- | Type-erased entry in a sub-select
foreign import data SubSelEntry :: Type
-- | Initialized, type-erased sub-select.
type SubSelect a =
    { id :: String
    , label :: String
    , select :: Select SubSelEntry
    , funcs :: AutoFillSelectFuncs SubSelEntry
    , selAff :: FormContext -> a -> SearchAff SubSelEntry
    , autoSel :: FormContext -> Maybe a -> AutoSelAction SubSelEntry
    }

initSubSelect :: forall a b. SubSelectDefiniton a b -> SubSelect a
initSubSelect { id, label, funcs, selAff: selAff', autoSel } =
    -- For more information on the technique used here, see `Widegets.Form.Dynamic`.
    { id
    , label
    , select: initSelect' (id <> "-af-select") emptySearchAff $ defSelectConfig
        { placeholder = Just $ lookupTranslation "select-choose-search" }
    , funcs:
        { apply: \fc -> funcs.apply fc <<< fromEntry
        , itemId: funcs.itemId <<< fromEntry
        , itemLabel
        , load: \fc -> map toEntry <<< funcs.load fc
        }
    , autoSel: \fc -> map toEntry <<< autoSel fc
    , selAff
    }
  where
    itemLabel = funcs.itemLabel <<< fromEntry

    selAff fc a = mapSearchAff toEntry rankByLabel $ selAff' fc a

    fromEntry :: SubSelEntry -> b
    fromEntry = unsafeCoerce
    toEntry :: b -> SubSelEntry
    toEntry = unsafeCoerce


-- | Widget type for autofill selects
newtype AutoFillSelect a = AutoFillSelect (AutoFillSelectType a)
instance showAFS :: Show (AutoFillSelect a) where
    show (AutoFillSelect s) = "AutoFillSelect " <> s.id

-- | Internal state of the autofill select.
type AutoFillSelectType a =
    { id :: String
    , select :: Select a
    , subsels :: Array (SubSelect a)
    , def :: AutoFillSelectDefinition a
    , fillAnyway :: Boolean
    -- ^ If this flag is set to true the widget will re-run the autofill even
    -- if the selection didn't change.
    }

-- | Gets the ID of the currently selected item.
afSelectedId :: forall a. AutoFillSelect a -> Maybe ID
afSelectedId (AutoFillSelect { def: { funcs: { itemId } }, select }) =
    itemId <$> _.value <$> selectedItem select


runAutoFillSelect :: forall a. FormContext -> AutoFillSelectType a
    -> Widget HTML (FormWidgetResult (AutoFillSelect a))
runAutoFillSelect fc oldState = do
    newState <- renderAutoFillSelect fc oldState
    let oldArgs = applyAutofillArgs fc oldState fc.autofillArgs
    let newArgs = applyAutofillArgs fc newState fc.autofillArgs
    pure if oldArgs == newArgs && not newState.fillAnyway
        -- If the autofill args didn't change, just update with the new state.
        then formWidgetState $ AutoFillSelect newState
        -- If they did change, trigger an autofill.
        else formWidgetAction (AutoFillSelect $ newState { fillAnyway = false }) $
                FWAutoFill newArgs

-- | Apply the selections in the given autofill select to the given autofill arguments.
applyAutofillArgs :: forall a. FormContext -> AutoFillSelectType a -> AutofillArgs -> AutofillArgs
applyAutofillArgs fc state = applyTypes <<< applySubSels <<< applyMainSel
  where
    applyTypes :: AutofillArgs -> AutofillArgs
    applyTypes = _ { types = state.def.types }
    applyMainSel :: AutofillArgs -> AutofillArgs
    applyMainSel = maybe noop (state.def.funcs.apply fc) $ map _.value $ selectedItem state.select
    applySubSels :: AutofillArgs -> AutofillArgs
    applySubSels af = foldl (flip applySubSel) af state.subsels
    applySubSel :: SubSelect a -> AutofillArgs -> AutofillArgs
    applySubSel sub = maybe noop (sub.funcs.apply fc) (_.value <$> selectedItem sub.select)
    noop x = x

renderAutoFillSelect :: forall a.
    FormContext -> AutoFillSelectType a -> Widget HTML (AutoFillSelectType a)
renderAutoFillSelect fc state = D.div
    [ P.className "autofill-select search-subform-search" ] $
    [ D.div
        [ P.className "search-subform-select-wrapper" ] $
        [ D.label [ P.htmlFor state.select.id ] [ D.text state.def.label ]
        , maybeApplyAutoSels <$> state { select = _ } <$> selectWidget selState
        ] <> if state.select.config.editable
            then map runButton (state.def.buttons $ map _.value $ selectedItem state.select)
            else []
    ] <> mapWithIndex handleSubSel state.subsels
  where
    selState :: Select a
    selState = setSearchAff state.select $ state.def.selAff fc

    handleSubSel :: Int -> SubSelect a -> Widget HTML (AutoFillSelectType a)
    handleSubSel idx sub = (\sub' -> state { subsels = fromMaybe [] $ updateAt idx sub' state.subsels }) <$>
        renderSubSelect fc state sub

    selectedId :: AutoFillSelectType a -> Maybe ID
    selectedId state' = state'.def.funcs.itemId <$> _.value <$> selectedItem state'.select

    maybeApplyAutoSels state' | selectedId state' == selectedId state = state'
    maybeApplyAutoSels state' =
        let selItem = _.value <$> selectedItem state'.select in
        state' { subsels = map (applyAutoSel selItem) state'.subsels }

    applyAutoSel :: Maybe a -> SubSelect a -> SubSelect a
    applyAutoSel sel sub = case sub.autoSel fc sel of
        AutoSelect ent ->
            sub { select = setSelected sub.select $ Just { label: sub.funcs.itemLabel ent, value: ent } }
        ASDeselect ->
            sub { select = setSelected sub.select Nothing }
        ASNoChange -> sub

    runButton :: AFSelectButton a -> Widget HTML (AutoFillSelectType a)
    runButton btn = do
        ent <- btn fc
        case map (\e -> { label: state.def.funcs.itemLabel e, value: e }) ent of
            Just ent' -> pure $ maybeApplyAutoSels $
                state { select = setSelected state.select $ Just ent', fillAnyway = true }
            Nothing -> pure state

renderSubSelect :: forall a. FormContext -> AutoFillSelectType a -> SubSelect a
    -> Widget HTML (SubSelect a)
renderSubSelect fc state subsel = D.div
    [ P.className "search-subform-select-wrapper" ]
    [ D.label [ P.htmlFor subsel.select.id ] [ D.text subsel.label ]
    , subsel { select = _ } <$> selectWidget selState
    ]
  where
    selState :: Select SubSelEntry
    selState = case selectedItem state.select of
        Just mainSel -> setSearchAff subsel.select $ subsel.selAff fc mainSel.value
        Nothing -> setSearchAff subsel.select emptySearchAff

instance afsFW :: FormWidget (AutoFillSelect a) where
    getId (AutoFillSelect { id }) = id
    isLeaf _ = true
    asField (AutoFillSelect { id }) = Just $ QScalar id
    fieldPaths fc (AutoFillSelect { id }) = [ fieldId fc id ]

    -- Render the select widget and pass its result to our wrapper
    toWidget fc (AutoFillSelect state) = runAutoFillSelect fc editableState
      where
        editable = fieldCanUpdate fc state.id

        setEditable :: forall b. Select b -> Select b
        setEditable sel = sel { config { editable = editable } }

        editableState = state
            { select = setEditable state.select
            , subsels = map (\sub -> sub { select = setEditable sub.select }) state.subsels
            }

    getData _ (AutoFillSelect state) =
        fromMaybe J.jsonNull (J.fromString <$> state.def.funcs.itemId <$> _.value <$> selectedItem state.select)
    setData _ state json | J.isNull json = Right state
    setData fc (AutoFillSelect state) json = wrapJsonError' json do
        obj <- assertObject json
        if O.member "id" obj
            then loadObj obj
            else pure $ AutoFillSelect state
      where
        loadObj obj = do
            id :: Maybe String <- (obj .:? "id") >>= case _ of
                Nothing -> pure Nothing
                Just id' -> Just <$> numberToString id'
            let oldSel = selectedItem state.select
            let mainSel = if (state.def.funcs.itemId <$> _.value <$> oldSel) == id
                then oldSel
                else wrapOpt state.def <$> (state.def.funcs.load fc =<< id)
            subsels <- traverse (loadSubSel obj) state.subsels
            pure $ AutoFillSelect $ state
                { select = setSelected state.select mainSel
                , subsels = subsels
                }

        wrapOpt :: forall r b. { funcs :: AutoFillSelectFuncs b | r } -> b -> SelectOption b
        wrapOpt { funcs } opt = { label: funcs.itemLabel opt, value: opt }
        loadSubSel obj sub = do
            id :: Maybe String <- (obj .:? (sub.id <> "Id")) >>= case _ of
                Nothing -> pure Nothing
                Just id' -> Just <$> numberToString id'
            if (sub.funcs.itemId <$> _.value <$> selectedItem sub.select) == id
                then pure sub
                else pure sub { select = setSelected sub.select $ map (wrapOpt sub) $ sub.funcs.load fc =<< id }
